import { Text, makeStyles, tokens } from '@fluentui/react-components';

/**
 * GanttView — Gantt plan-vs-real genérico (no atado a F2).
 *
 * Cada fila pinta DOS barras superpuestas en la misma pista temporal: la
 * planeada (siempre gris, referencia) y la real (coloreada por
 * `deviationDays` — atrasado/adelantado/a tiempo). Una fila sin `actualStart`
 * simplemente no dibuja la barra real (etapa aún no iniciada).
 */

export interface GanttRow {
  id: string;
  label: string;
  /** `YYYY-MM-DD` */
  plannedStart: string;
  plannedEnd: string;
  actualStart?: string;
  actualEnd?: string;
  /** real − plan, en días. Positivo = atrasado, negativo = adelantado. */
  deviationDays?: number;
}

interface GanttViewProps {
  rows: GanttRow[];
  plannedLabel: string;
  actualLabel: string;
  onTimeLabel: string;
  lateLabel: string;
  earlyLabel: string;
}

const DAY_MS = 86_400_000;
const ON_TIME_THRESHOLD_DAYS = 3;

function toDays(d: string): number {
  return new Date(`${d}T00:00:00Z`).getTime() / DAY_MS;
}

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  legend:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  legendSwatch: { width: '12px', height: '12px', borderRadius: '3px', flexShrink: 0 },
  row:     { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  label:   { width: '160px', flexShrink: 0, color: tokens.colorNeutralForeground2 },
  track:   { position: 'relative', flex: 1, height: '26px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusSmall },
  plannedBar: {
    position: 'absolute', top: '4px', height: '8px', borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke1, minWidth: '2px',
  },
  actualBar: {
    position: 'absolute', bottom: '4px', height: '8px', borderRadius: tokens.borderRadiusSmall,
    minWidth: '2px',
  },
});

function deviationColor(deviationDays: number | undefined): string {
  if (deviationDays == null) return tokens.colorNeutralStroke2;
  if (deviationDays > ON_TIME_THRESHOLD_DAYS) return tokens.colorPaletteRedBackground3;
  if (deviationDays < -ON_TIME_THRESHOLD_DAYS) return tokens.colorPaletteBlueBackground2;
  return tokens.colorPaletteGreenBackground3;
}

export function GanttView({ rows, plannedLabel, actualLabel, onTimeLabel, lateLabel, earlyLabel }: GanttViewProps) {
  const s = useStyles();
  if (rows.length === 0) return null;

  const allDates = rows.flatMap(r => [r.plannedStart, r.plannedEnd, r.actualStart, r.actualEnd].filter(Boolean) as string[]);
  const min = Math.min(...allDates.map(toDays));
  const max = Math.max(...allDates.map(toDays));
  const span = Math.max(1, max - min);
  const pctOf = (d: string) => ((toDays(d) - min) / span) * 100;

  return (
    <div className={s.root}>
      <div className={s.legend}>
        <span className={s.legendItem}><span className={s.legendSwatch} style={{ backgroundColor: tokens.colorNeutralStroke1 }} />{plannedLabel}</span>
        <span className={s.legendItem}><span className={s.legendSwatch} style={{ backgroundColor: tokens.colorPaletteGreenBackground3 }} />{actualLabel} — {onTimeLabel}</span>
        <span className={s.legendItem}><span className={s.legendSwatch} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }} />{lateLabel}</span>
        <span className={s.legendItem}><span className={s.legendSwatch} style={{ backgroundColor: tokens.colorPaletteBlueBackground2 }} />{earlyLabel}</span>
      </div>
      {rows.map(row => {
        const plannedLeft = pctOf(row.plannedStart);
        const plannedWidth = Math.max(pctOf(row.plannedEnd) - plannedLeft, 0.5);
        const actualLeft = row.actualStart ? pctOf(row.actualStart) : null;
        const actualEndPct = row.actualEnd ? pctOf(row.actualEnd) : (row.actualStart ? pctOf(row.actualStart) : null);
        const actualWidth = actualLeft != null && actualEndPct != null ? Math.max(actualEndPct - actualLeft, 0.5) : 0;

        return (
          <div key={row.id} className={s.row}>
            <Text size={200} className={s.label}>{row.label}</Text>
            <div className={s.track}>
              <div className={s.plannedBar} style={{ left: `${plannedLeft}%`, width: `${plannedWidth}%` }} />
              {actualLeft != null && (
                <div
                  className={s.actualBar}
                  style={{ left: `${actualLeft}%`, width: `${actualWidth}%`, backgroundColor: deviationColor(row.deviationDays) }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
